<template>
  <div>
    <q-expansion-item
      label="Search Filters"
      color="primary"
      switch-toggle-side
      expand-separator
      class="q-mb-md"
      :dense="true"
      expand-icon="expand_more"
      expand-icon-class="text-primary"
    >
      <div class="q-pa-md">
        <!-- Dropdown Filters Row -->
        <div class="row q-col-gutter-md q-mb-md">
          <!-- Customer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.customer_id"
              :options="customerOptions"
              label="Customer"
              outlined
              dense
            />
          </div>

          <!-- Producer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.producer_id"
              :options="producerOptions"
              label="Producer"
              outlined
              dense
            />
          </div>

          <!-- Hauler Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.hauler_id"
              :options="haulerOptions"
              label="Hauler"
              outlined
              dense
            />
          </div>
        </div>

        <!-- Date/Ticket -->
        <div class="row q-col-gutter-md q-mb-md">
          <!-- Ticket Number -->
          <div class="col-4">
            <q-input
              v-model="filters.ticketNumber"
              label="Ticket Number"
              outlined
              dense
            />
          </div>
          <div class="col-8">
            <div class="clickable-text">
              <q-icon name="event" size="md" color="primary" class="cursor-pointer" />
              <span>Select Date Range</span>
            </div>
            <q-popup-proxy v-model="showFilterDatePicker" transition-show="scale" transition-hide="scale">
              <q-date
                v-model="filters.dateRange"
                range
                :options="restrictDates"
                mask="YYYY-MM-DD"
                class="q-pa-md"
              >
                <div class="row items-center justify-end q-gutter-sm">
                  <q-btn label="Clear" color="primary" @click="filters.dateRange = null" />
                </div>
              </q-date>
            </q-popup-proxy>
          </div>
        </div>

        <!-- Apply Filters Button -->
        <q-btn
          label="Apply Filters"
          color="primary"
          @click="fetchTicketCrops"
          class="q-mt-md"
        />
        <q-btn label="Reset"
          type="reset"
          @click="clearFilters"
          color="primary"
          flat
          class="q-ml-sm">
        </q-btn>
      </div>
    </q-expansion-item>
    <!-- Collapsible Search/Filter Section -->
    <div class="q-mt-md">
      <div class="row-reverse">
        <q-btn
          color="primary"
          label="Edit Selected"
          @click="openModal"
          class="q-mb-md q-ml-lg"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
        <q-btn
          color="red"
          label="Delete Selected"
          @click="openDeleteModal"
          class="q-mb-md q-ml-lg"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
      </div>
    </div>

    <BaseTable
      :rows="formattedTicketCrops"
      :columns="columns"
      :total-rows="totalTicketCrops"
      search-placeholder="Search Current Table..."
    >
      <template #actions="{ row }">
        <q-td class="">
          <q-checkbox class="q-mr-sm"
            v-model="selectedRows"
            :val="row.id"
            dense
          />
        </q-td>
      </template>
    </BaseTable>

    <!-- Pagination Control (bottom of table) -->
    <div class="row justify-left q-my-md q-ml-lg">
      <q-pagination
        v-model="currentPage"
        :max="totalPages"
        @update:model-value="onPageChange"
        color="primary"
        direction-links
        :max-pages="10"
      />
    </div>

    <!-- Modal for Editing Selected Rows -->
    <q-dialog v-model="showModal">
      <q-card class="q-pa-md">
        <q-card-section>
          <div class="text-h6">Editing {{ selectedRows.length }} Tickets</div>
        </q-card-section>

        <q-form ref="editFormRef" @submit.prevent="submitEditForm">
          <q-card-section>
            <q-input v-model.number="editForm.at_moisture" label="At Moisture"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.moisture" label="Moisture"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.customer_rate" label="Customer Rate"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.producer_rate" label="Producer Rate"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.haul_rate" label="Haul Rate"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.haul_mile_rate" label="Haul Mile Rate"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.haul_mile" label="Haul Mile"
                     :rules="[validateNumeric]" :dense="true" />
            <q-input v-model.number="editForm.min_mile" label="Min Mile"
                     :rules="[validateNumeric]" :dense="true" />
          </q-card-section>

          <q-card-actions align="right">
            <q-btn label="Cancel" color="negative" @click="showModal = false" flat />
            <q-btn label="Submit" type="submit" color="primary" />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
    <q-dialog v-model="deleteModalVisible">
      <q-card>
        <q-card-section>
          <div class="text-h6">
            Confirm Deletion
          </div>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete <b>{{ selectedRows.length }}</b> ticket(s)?</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" @click="cancelBulkDelete" />
          <q-btn flat label="Delete" color="negative" @click="confirmBulkDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import { watch } from 'vue';
import BaseTable from '@/components/BaseTable';
import { selectedYear } from '@/store/store';

export default {
  name: 'TicketCropTable',
  components: {
    BaseTable
  },
  data() {
    const query_params = new URLSearchParams(window.location.search);

    return {
      // Table Data
      ticketCrops: [],
      // Totals
      totalTicketCrops: 0,

      // Pagination State
      currentPage: 1,
      pageSize: 50,
      totalPages: 1,

      // Selections / Modals
      selectedRows: [],
      showModal: false,
      deleteModalVisible: false,
      showFilterDatePicker: false,

      // Form data
      editForm: {
        at_moisture: null,
        moisture: null,
        customer_rate: null,
        producer_rate: null,
        haul_rate: null,
        haul_mile_rate: null,
        haul_mile: null,
        min_mile: null,
        ids: []
      },

      // Filters
      filters: {
        ticketNumber: null,
        dateRange: null,
        customer_id: null,
        producer_id: null,
        hauler_id: null,
        job_id: query_params.get('job_id') || null,
      },

      // Dropdown options
      customerOptions: [],
      producerOptions: [],
      haulerOptions: [],

      // Table columns
      columns: [
        { name: 'actions', align: 'center', label: '', field: 'actions', sortable: false },
        { name: 'ticket_number', align: 'center', label: 'Ticket Number', field: 'ticket_number', sortable: true },
        { name: 'date', align: 'left', label: 'Date', field: 'date', sortable: true },
        { name: 'hauler_id', align: 'left', label: 'Hauler', field: 'hauler_id', sortable: true },
        { name: 'truck_id', align: 'left', label: 'Truck', field: 'truck_id', sortable: true },
        { name: 'customer_id', align: 'left', label: 'Customer', field: 'customer_id', sortable: true },
        { name: 'producer_id', align: 'left', label: 'Producer', field: 'producer_id', sortable: true },
        { name: 'gross_weight', align: 'left', label: 'Gross Weight', field: 'gross_weight', sortable: true },
        { name: 'tare_weight', align: 'left', label: 'Tare Weight', field: 'tare_weight', sortable: true },
        { name: 'at_moisture', align: 'left', label: 'At Moisture', field: 'at_moisture', sortable: true },
        { name: 'moisture', align: 'left', label: 'Moisture', field: 'moisture', sortable: true },
        { name: 'customer_rate', align: 'left', label: 'Customer Rate', field: 'customer_rate', sortable: true },
        { name: 'producer_rate', align: 'left', label: 'Producer Rate', field: 'producer_rate', sortable: true },
        { name: 'haul_rate', align: 'left', label: 'Haul Rate', field: 'haul_rate', sortable: true },
        { name: 'haul_mile_rate', align: 'left', label: 'Haul Mile Rate', field: 'haul_mile_rate', sortable: true },
        { name: 'haul_mile', align: 'left', label: 'Haul Mile', field: 'haul_mile', sortable: true },
        { name: 'min_mile', align: 'left', label: 'Min Mile', field: 'min_mile', sortable: true },
        { name: 'edit_date', align: 'left', label: 'Edit Date', field: 'edit_date', sortable: true }
      ]
    };
  },
  setup() {
    // Restrict date selection to the selectedYear
    const restrictDates = (date) => {
      let year = selectedYear.value;
      const start = `${year}/01/01`;
      const end = `${year}/12/31`;
      return date >= start && date <= end;
    };
    return {
      restrictDates
    };
  },
  computed: {
    formattedTicketCrops() {
      return this.ticketCrops.map(ticket => ({
        ...ticket,
        job_id: ticket.job_id?.id || 'N/A',
        truck_id: ticket.truck_id?.name || 'N/A',
        hauler_id: ticket.truck_id?.hauler_id?.name || 'N/A',
        customer_id: ticket.customer_id?.name || 'N/A',
        producer_id: ticket.producer_id?.name || 'N/A',
        date: new Date(ticket.date).toLocaleDateString(),
        edit_date: new Date(ticket.edit_date).toLocaleDateString()
      }));
    }
  },
  created() {
    // Fetch data, filters, and watch year changes
    this.fetchTicketCrops();
    this.fetchFilterOptions();

    watch(selectedYear, () => {
      // Reset page to 1 if the year changes
      this.currentPage = 1;
      this.fetchTicketCrops();
    });
  },
  methods: {
    async fetchFilterOptions() {
      try {
        const [customers, producers, haulers] = await Promise.all([
          axios.get('/api/v1/customer/customers/'),
          axios.get('/api/v1/producer/producers/'),
          axios.get('/api/v1/hauler/haulers/')
        ]);

        this.customerOptions = customers.data.map(item => ({ label: item.name, value: item.id }));
        this.producerOptions = producers.data.map(item => ({ label: item.name, value: item.id }));
        this.haulerOptions = haulers.data.map(item => ({ label: item.name, value: item.id }));
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    },

    async fetchTicketCrops() {
      console.log(this.filters);
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize,
          year: selectedYear.value,
          ...(this.filters.ticketNumber && { ticket_number: this.filters.ticketNumber }),
          ...(this.filters.dateRange && {
            date_start: this.filters.dateRange.from,
            date_end: this.filters.dateRange.to,
          }),
          ...(this.filters.customer_id && { customer_id: this.filters.customer_id.value }),
          ...(this.filters.producer_id && { producer_id: this.filters.producer_id.value }),
          ...(this.filters.hauler_id && { hauler_id: this.filters.hauler_id.value }),
          ...(this.filters.job_id && { job_id: this.filters.job_id }),
        };

        const response = await axios.get('/api/v1/ticket_crop/ticket-crop/', { params });

        // If using your CustomPagination, DRF returns:
        //  { count, total_pages, next, previous, results }
        this.ticketCrops = response.data.results;
        this.totalTicketCrops = response.data.count;
        // Or set totalPages from the server's "total_pages"
        this.totalPages = response.data.total_pages;
      } catch (error) {
        console.error('Error fetching ticket crops:', error);
      }
    },

    // Called when user changes page via the <q-pagination> control
    onPageChange(page) {
      this.currentPage = page;
      this.fetchTicketCrops();
    },

    openModal() {
      if (this.selectedRows.length < 1) {
        return;
      }
      this.showModal = true;

      const selectedRowFirst = this.ticketCrops.find(
        crop => crop.id === this.selectedRows[0]
      );

      if (!selectedRowFirst) {
        console.error('Selected row not found in ticketCrops');
        return;
      }

      // Populate editForm from the first selected row
      this.editForm = {
        at_moisture: selectedRowFirst.at_moisture ?? null,
        moisture: selectedRowFirst.moisture ?? null,
        customer_rate: selectedRowFirst.customer_rate ?? null,
        producer_rate: selectedRowFirst.producer_rate ?? null,
        haul_rate: selectedRowFirst.haul_rate ?? null,
        haul_mile_rate: selectedRowFirst.haul_mile_rate ?? null,
        haul_mile: selectedRowFirst.haul_mile ?? null,
        min_mile: selectedRowFirst.min_mile ?? null
      };
    },

    validateNumeric(value) {
      return (value !== null && value !== '' && !isNaN(value))
        || 'This field must be a numeric value';
    },

    async submitEditForm() {
      this.$refs.editFormRef.validate().then(async (success) => {
        if (success) {
          try {
            this.editForm.ids = this.selectedRows;

            await axios.put(
              '/api/v1/ticket_crop/ticket-crop/bulk-update/',
              this.editForm
            );

            this.$q.notify({
              color: 'green',
              message: 'Crop ticket updated successfully!',
              icon: 'check'
            });

            this.showModal = false;
            this.fetchTicketCrops();
          } catch (error) {
            console.error('Error submitting form:', error);
            this.$q.notify({
              color: 'red',
              message: 'Failed to update crop tickets.',
              icon: 'error'
            });
          }
        }
      });
    },

    openDeleteModal() {
      if (this.selectedRows.length < 1) {
        this.$q.notify({
          color: 'red',
          message: 'No tickets selected to delete.',
          icon: 'error'
        });
        return;
      }
      this.deleteModalVisible = true;
    },

    async confirmBulkDelete() {
      try {
        const response = await axios.delete(
          '/api/v1/ticket_crop/ticket-crop/bulk-delete/',
          { data: { ids: this.selectedRows } }
        );

        this.$q.notify({
          color: 'green',
          message: response.data.message,
          icon: 'check'
        });

        // Refresh data, close modal
        this.fetchTicketCrops();
        this.deleteModalVisible = false;
        this.selectedRows = [];
      } catch (error) {
        console.error('Error during bulk delete:', error);
        this.$q.notify({
          color: 'red',
          message: 'Failed to delete crop tickets.',
          icon: 'error'
        });
      }
    },

    cancelBulkDelete() {
      this.deleteModalVisible = false;
    },

    clearFilters() {
      this.filters = {
        ticketNumber: null,
        dateRange: null,
        customer_id: null,
        producer_id: null,
        hauler_id: null,
      };
      // Reset page to 1 on filter clear
      this.currentPage = 1;
      this.fetchTicketCrops();
    },
  }
};
</script>

<style scoped>
/* Add specific styles for the TicketCropTable here if needed */
</style>
